<template>
    <div
        class="rt-row"
        :class="{ 'rt-row_overdue': isOverdue }"
        :style="{
            'gridTemplateColumns': template,
            'minWidth': minWidth,
            '--rt-row-state-color': stateColor,
            '--list-text-lines': textLines,
        }"
    >
        <div
            class="rt-row__cell"
            :class="cellClass(index)"
            :style="cellStyle(index)"
            :key="column.key"
            v-for="(column, index) in columns"
        >
            <template v-if="column.key === 'id'">
                <span class="rt-row__id">{{ request.id }}</span>
            </template>

            <template v-else-if="column.key === 'description'">
                <span class="rt-row__description-text" :title="rowText">{{ rowText }}</span>
                <span class="rt-pill rt-pill_unread" v-if="rowUnread > 0">{{ rowUnread }}</span>
            </template>

            <template v-else-if="column.key === 'status'">
                <span class="rt-pill" :class="'rt-pill_' + rowStatus.className">{{ rowStatus.title }}</span>
            </template>

            <template v-else-if="column.key === 'priority'">
                <RequestPriorityBadge :priority="request.priority"></RequestPriorityBadge>
            </template>

            <template v-else-if="column.key === 'author'">
                <div class="rt-row__user">
                    <img
                        class="rt-row__avatar"
                        :src="avatarUrl(request.author)"
                        v-if="avatarUrl(request.author)"
                        alt=""
                    />
                    <span class="rt-row__avatar rt-row__avatar_initials" v-else>{{
                        initials(request.author && request.author.displayName)
                    }}</span>
                    <span class="rt-row__user-name" :title="request.author && request.author.displayName">{{
                        (request.author && request.author.displayName) || '—'
                    }}</span>
                </div>
            </template>

            <template v-else-if="column.key === 'executor'">
                <div class="rt-row__user" v-if="request.executor">
                    <img
                        class="rt-row__avatar"
                        :src="avatarUrl(request.executor)"
                        v-if="avatarUrl(request.executor)"
                        alt=""
                    />
                    <span class="rt-row__avatar rt-row__avatar_initials" v-else>{{
                        initials(request.executor.displayName)
                    }}</span>
                    <span class="rt-row__user-name" :title="request.executor.displayName">{{
                        request.executor.displayName
                    }}</span>
                </div>
                <div class="rt-row__user" v-else>
                    <span class="rt-row__user-name" :title="request.securityGroup && request.securityGroup.name">{{
                        request.securityGroup ? request.securityGroup.name : '—'
                    }}</span>
                </div>
            </template>

            <template v-else-if="column.key === 'deadline'">
                <span class="rt-row__deadline" :class="'rt-row__deadline_' + deadlineClass" :title="deadlineTitle">{{
                    deadlineText
                }}</span>
            </template>

            <template v-else-if="column.key === 'created'">
                <span>{{ createdText }}</span>
            </template>

            <template v-else-if="column.key === 'service'">
                <span class="rt-row__ellipsis" :title="request.service && request.service.title">{{
                    request.service ? request.service.title : '—'
                }}</span>
            </template>

            <template v-else-if="column.key === 'category'">
                <span class="rt-row__ellipsis" :title="request.category && request.category.title">{{
                    request.category ? request.category.title : '—'
                }}</span>
            </template>

            <template v-else-if="column.key === 'city'">
                <span class="rt-row__ellipsis" :title="request.city && request.city.name">{{
                    request.city ? request.city.name : '—'
                }}</span>
            </template>

            <template v-else-if="column.key === 'group'">
                <span class="rt-row__ellipsis" :title="request.securityGroup && request.securityGroup.name">{{
                    request.securityGroup ? request.securityGroup.name : '—'
                }}</span>
            </template>

            <template v-else-if="column.key === 'activity'">
                <span>{{ activityText }}</span>
            </template>

            <template v-else-if="column.key === 'unread'">
                <span>{{ rowUnread > 0 ? rowUnread : '—' }}</span>
            </template>
        </div>
    </div>
</template>
<script>
import {
    gridTemplate,
    gridMinWidthCss,
    stickyOffsetCss,
    stickyOffsets,
    lastStickyIndex,
    description,
    lastMessage,
    statusView,
    unreadCount,
    deadlineView,
    stateColor as requestStateColor,
    avatarUrl,
    initials,
} from './requestRowData';
import RequestPriorityBadge from '../RequestPriority/RequestPriorityBadge.vue';

export default {
    components: { RequestPriorityBadge },
    props: {
        columns: { type: Array, required: true },
        request: { type: Object, required: true },
        textLines: { type: Number, default: 1 },
        textColumnSource: { type: String, default: 'description' },
    },
    computed: {
        template() {
            return gridTemplate(this.columns);
        },
        minWidth() {
            return gridMinWidthCss(this.columns);
        },
        stateColor() {
            return requestStateColor(this.request);
        },
        // Обе функции остаются в requestRowData.js — переключается только то, какая
        // из них здесь вызвана, по тому же account-settings-ключу, что и заголовок
        // колонки в RequestStore.vue (иначе заголовок и содержимое разойдутся).
        rowText() {
            return this.textColumnSource === 'last-message' ? lastMessage(this.request) : description(this.request);
        },
        rowStatus() {
            return statusView(this.request);
        },
        rowUnread() {
            return unreadCount(this.request, this.$_account && this.$_account.id);
        },
        createdText() {
            return this.$_toLocalDateShort(this.request.createdAt);
        },
        activityText() {
            return this.$_toLocalDateShort(this.request.updatedAt);
        },
        rowDeadline() {
            return deadlineView(this.request, this.$_toLocalDateShort);
        },
        deadlineText() {
            return this.rowDeadline.text;
        },
        deadlineClass() {
            return this.rowDeadline.className;
        },
        deadlineTitle() {
            return this.rowDeadline.title;
        },
        // Просрочка — то же условие, по которому колонка «Срок» пишет «просрочено на …»,
        // а не отдельная проверка: два источника правды разошлись бы на границе суток.
        isOverdue() {
            return this.deadlineClass === 'over';
        },
        offsets() {
            return stickyOffsets(this.columns);
        },
        lastSticky() {
            return lastStickyIndex(this.columns);
        },
    },
    methods: {
        avatarUrl,
        initials,
        cellClass(index) {
            return {
                'rt-row__cell_sticky': this.offsets[index] !== null,
                'rt-row__cell_sticky-last': index === this.lastSticky,
            };
        },
        cellStyle(index) {
            const offset = this.offsets[index];
            return offset === null ? {} : { left: stickyOffsetCss(offset, index) };
        },
    },
};
</script>
<style lang="scss">
.rt-pill {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 11px;
    font: 600 0.6875rem/1 var(--font-family);
    white-space: nowrap;

    &_new {
        background: #00a55a;
        color: #fff;
    }

    &_work {
        background: #e8f5e9;
        color: #2e7d32;
    }

    &_done {
        background: #fff;
        border: 1px solid #a5d6a7;
        color: #2e7d32;
    }

    &_wait {
        background: #fdf0e7;
        color: #d9641e;
    }

    &_closed {
        background: #f1f3f4;
        color: #80868b;
    }

    &_unread {
        min-width: 18px;
        height: 18px;
        margin-left: 8px;
        padding: 0 6px;
        border-radius: 9px;
        background: #00a55a;
        color: #fff;
        font-size: 0.625rem;
    }
}

// Подсветка обращения, открытого в панели справа. Зелёный, оранжевый и серый в этом
// списке заняты статусами, поэтому выделение взято вне статусной палитры: холодный
// сине-серый ни на один статус не похож и не прочитается как свойство заявки.
$rt-selected-bg: #eef2f8;
$rt-selected-bg-hover: #e3ebf7;
$rt-selected-line: #dde5f2;

// Просрочка — единственное состояние, которое красит строку целиком. Полосы слева и
// красного текста в дальней колонке «Срок» мало: при прокрутке сотни строк взгляд их
// не ловит. Заливка светлая — строка должна выделяться на фоне соседей, а не спорить
// с собственным текстом; сам красный остаётся на полосе и на сроке.
$rt-overdue-bg: #fdecea;
$rt-overdue-bg-hover: #fbe0dd;
$rt-overdue-line: #f7d4d0;

.rt-row {
    display: grid;
    position: relative;
    align-items: center;
    // font-size продублирован тут же, где живёт --list-line-height: сам множитель
    // (1.3) — не длина, а calc ниже переводит его в px через 1em. Без явного
    // font-size на .rt-row тот же em считался бы от унаследованного размера — не
    // от 14px, которым реально набран текст в .rt-row__cell — и высота строки
    // разъехалась бы с текстом, который она должна вмещать.
    font-size: 0.875rem;
    --list-line-height: 1.3;
    // Было 48px константой (под 13px-текст и 12px паддинг ячейки). Задача —
    // плотнее таблица, поэтому формула теперь считает высоту с нуля, без старого
    // якоря: 20px — тот же вертикальный запас, что и раньше (2×12 → 2×10, тем же
    // множителем, что горизонтальный паддинг ячейки ниже). При --list-text-lines: 1
    // высота падает с 48px до ~38.2px (1×1.3×14 + 20) — это и есть цель правки.
    // Шрифт крупнее, паддинг меньше — при 3 строках (74.6px) итог почти не сдвинулся
    // относительно прежних 74.7px: искали именно так, а не разом на обоих полюсах.
    min-height: calc(var(--list-text-lines, 1) * var(--list-line-height) * 1em + 20px);
    // Непрозрачный фон: закреплённые ячейки наследуют его и перекрывают
    // проезжающие под ними колонки.
    background: #fff;
    border-bottom: 1px solid #f1f3f4;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.12s ease-out;

    &:hover {
        background: #f6f7f8;
    }

    // Идёт после :hover намеренно: у `.rt-row_overdue:hover` и `.rt-row:hover`
    // одинаковая специфичность, и побеждает тот, кто ниже. Закреплённые ячейки
    // берут фон через `background: inherit` и уезжают вместе со всей строкой.
    &_overdue {
        background: $rt-overdue-bg;
        border-bottom-color: $rt-overdue-line;

        &:hover {
            background: $rt-overdue-bg-hover;
        }

        .rt-row__cell_sticky-last {
            border-right-color: $rt-overdue-line;
        }
    }

    &__cell {
        display: flex;
        position: relative;
        align-items: center;
        // Строка выравнивает ячейки по центру, из-за чего ячейка была высотой в контент.
        // Полосе состояния и фону закреплённых ячеек нужна вся высота строки.
        align-self: stretch;
        padding: 0 10px;
        overflow: hidden;
        font: 400 0.875rem/1.3 var(--font-family);
        color: #3c4043;
        white-space: nowrap;

        // Полоса состояния живёт на первой ячейке, а не на строке: если первая
        // колонка закреплена, полоса едет вместе с ней и не уходит за левый край.
        &:first-child::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            width: 3px;
            background: var(--rt-row-state-color, transparent);
        }

        &_sticky {
            position: sticky;
            z-index: $layer-sticky-cell;
            background: inherit;
        }

        // Граница закреплённой группы: стоит на последней липкой ячейке, поэтому
        // остаётся на месте, а не уезжает вместе с прокручиваемыми колонками.
        &_sticky-last {
            border-right: 1px solid #e8eaed;
        }

        // Последняя колонка отбивается от предыдущей сильнее: справа её держит край
        // строки, а слева при обычных 10px она льнула к соседке. Та же величина
        // продублирована в шапке — иначе заголовок разъедется со значением.
        &:last-child {
            padding-left: 20px;
        }
    }

    &__id {
        // Табличные цифры (tnum есть в Factor B): моношрифта в проекте нет, а колонка №
        // должна оставаться выровненной по разрядам.
        font-family: var(--font-family);
        font-variant-numeric: tabular-nums;
        color: #80868b;
    }

    // --list-text-lines управляет и обрезкой, и высотой строки (min-height выше) —
    // одна и та же переменная, чтобы они не могли разойтись. line-clamp работает и
    // при 1 строке: отдельного пути через white-space: nowrap не заводим, иначе
    // 1 и 3 строки — это два разных механизма усечения, которые будут расходиться.
    // Один класс, а не два: колонка теперь одна, содержимое переключается настройкой
    // источника текста (описание/последнее сообщение), а не двумя соседними колонками.
    &__description-text {
        overflow: hidden;
        white-space: normal;
        flex: 1 1 auto;
        min-width: 0;
        line-height: var(--list-line-height);
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: var(--list-text-lines, 1);
        line-clamp: var(--list-text-lines, 1);

        // white-space: normal (нужен для переноса при line-clamp) сам по себе не
        // рвёт слово без пробелов — такое слово (длинный URL, серийник) раздвинуло бы
        // ячейку вбок. overflow-wrap разрешает перенос и посреди слова, если по-другому
        // оно не помещается.
        overflow-wrap: anywhere;
    }

    // Браузеры без -webkit-line-clamp (в проекте это означает — без поддержки вообще,
    // см. caniuse): многоточия не будет, текст переносится и обрезается по высоте.
    // Та же формула (line-height × число строк), что и в min-height строки — иначе
    // фолбэк и line-clamp-версия отрежут текст на разной высоте.
    @supports not (-webkit-line-clamp: 1) {
        &__description-text {
            max-height: calc(var(--list-text-lines, 1) * var(--list-line-height) * 1em);
        }
    }

    &__user {
        display: flex;
        align-items: center;
        min-width: 0;
    }

    &__avatar {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        margin-right: 8px;
        border-radius: 50%;
        object-fit: cover;

        &_initials {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #e8eaed;
            color: #5f6368;
            font-size: 10px;
            font-weight: 600;
        }
    }

    &__user-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    // Справочные значения (услуга, категория, город, группа) в колонке фиксированной
    // ширины: длинное название обрезается многоточием, полное — в подсказке.
    &__ellipsis {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__deadline {
        &_over {
            color: #d93025;
            font-weight: 600;
        }

        &_soon {
            color: #d9641e;
            font-weight: 600;
        }

        &_dim {
            color: #9aa0a6;
        }
    }
}

// Класс проставляет сам RouterLink, обёрнутый вокруг строки. Правила идут после
// базовых: у `.router-link-exact-active .rt-row` и `.rt-row:hover` одинаковая
// специфичность, и порядок здесь решает, кто победит.
.router-link-exact-active .rt-row {
    background: $rt-selected-bg;
    border-bottom-color: $rt-selected-line;

    // Наведение не должно гасить выделение: подсвеченная строка под курсором
    // становится насыщеннее, а не переключается на нейтральный ховер.
    &:hover {
        background: $rt-selected-bg-hover;
    }

    .rt-row__cell_sticky-last {
        border-right-color: $rt-selected-line;
    }

    // Почти чёрный текст: метка переживает и тусклый монитор, и дальтонизм.
    // Только на ячейке — цвета срока принадлежат состоянию заявки и не перекрываются,
    // потому что заданы на самом элементе, а не наследуются.
    .rt-row__cell {
        color: #202124;
    }

    .rt-row__id {
        color: #3c4043;
        font-weight: 500;
    }
}
</style>
